import { useTranslation } from 'react-i18next';
import { Font, Indicator, ProductSelectionCard } from 'is24-corecss';
import type { FieldElement } from 'react-hook-form';
import styles from './ProductTiles.styles';
import type { ADACData, Plan, PlanType } from '@/components/content/welcomeModal/adacUpsell/model';
import { usePriceFormatterHook } from '@/components/content/welcomeModal/adacUpsell/usePriceFormatter.hook';

type Props = Pick<ADACData, 'plans'> & {
  selectedProduct: PlanType;
  onProuctSelect: (type: PlanType) => void;
} & Pick<FieldElement, 'className'>;

export const ProductTiles = ({ plans, onProuctSelect, selectedProduct, className }: Props) => {
  const { t } = useTranslation();

  return (
    <fieldset className={className}>
      <legend className='screenreader-only'>{t('common.adacModal.plans.common.selection')}</legend>
      <div className='grid grid-flex gutter-l'>
        {plans.map((plan, id) => (
          <ProductTile
            key={plan.id}
            plan={plan}
            previousPlan={id > 0 ? plans[id - 1] : undefined}
            isSelected={selectedProduct === plan.id}
            onSelect={onProuctSelect}
          />
        ))}
      </div>
    </fieldset>
  );
};

const ProductTile = ({
  plan,
  previousPlan,
  isSelected,
  onSelect,
}: {
  plan: Plan;
  previousPlan?: Plan;
  isSelected: boolean;
  onSelect: (id: PlanType) => void;
}) => {
  const { t } = useTranslation();
  const { formatPrice } = usePriceFormatterHook();

  const price = plan.discountedPrice ?? plan.originalPrice;
  const monthlyPrice = plan.id === 'yearly' ? price.amount / 12 : price.amount;
  const dailyPrice = formatPrice({ ...price, amount: monthlyPrice / 31 });
  const previousPrice = previousPlan?.discountedPrice ?? previousPlan?.originalPrice;
  // Consider other implementation - currently we assume a strict order of smaller to larger
  const savingsComparedToPrevious = previousPrice
    ? Math.round((previousPrice.amount * 12 - price.amount) * 100) / 100
    : undefined;

  const savingsLabel =
    savingsComparedToPrevious !== undefined ? (
      <Indicator asSpan appearance='tag' color='red'>
        {t('common.adacModal.plans.yearly.savings', {
          savings: formatPrice({ ...price, amount: savingsComparedToPrevious }),
        })}
      </Indicator>
    ) : undefined;

  return (
    <div className='grid-item one-half'>
      <ProductSelectionCard
        className={`${styles.productTile} height-full cursor-pointer`}
        radio
        name='adac-plan'
        checked={isSelected}
        onChange={() => onSelect(plan.id)}
        onClick={(event) => {
          // Radio changes are reported by onChange; other card clicks select the plan here.
          if (!(event.target instanceof HTMLInputElement)) onSelect(plan.id);
        }}
        title={
          <Font tag='span' variant='font-heading-xsmall-bold'>
            {t(`common.adacModal.plans.${plan.id}.name`)}
          </Font>
        }
        subline={
          <Font variant='font-body-medium-regular' className={`${styles.minimumTerm} margin-top-m margin-bottom-m`}>
            {t('common.adacModal.plans.common.minimum-term')}
          </Font>
        }
        price={<span className='font-nowrap'>{formatPrice(price)}</span>}
        priceSeparator={false}
        period={
          <Font tag='span' variant='font-body-small-regular' className={styles.mutedText}>
            / <span>{t(`common.adacModal.plans.${plan.id}.billing-period`)}</span>
          </Font>
        }
        priceCaption={<DailyPriceBadge dailyPrice={dailyPrice} />}
        discountPrice={
          plan.discountedPrice ? (
            <span className={`${styles.mutedText} font-body-small-regular`}>{formatPrice(plan.originalPrice)}</span>
          ) : undefined
        }
        label={savingsLabel}
        cta={false}
      />
    </div>
  );
};

export const DailyPriceBadge = ({ dailyPrice }: { dailyPrice: string }) => {
  const { t } = useTranslation();
  return (
    <Indicator asSpan size='small' appearance='tag' color='red' className='margin-vertical-s'>
      {t('common.adacModal.plans.common.daily-price', { price: dailyPrice })}
    </Indicator>
  );
};
